<template>
  <Modal title="Prospecting run history" :max-width="560" @close="$emit('close')">
    <div v-if="loading && runs.length === 0" class="emptyState">Loading…</div>
    <div v-else-if="runs.length === 0" class="emptyState">No prospecting runs yet.</div>

    <div v-else class="runList">
      <router-link
        v-for="run in runs"
        :key="run.id"
        :to="`/agent-runs/${run.id}`"
        class="runEntry"
      >
        <div class="runEntryHeader">
          <div v-if="run.account_domain || run.account_name" class="runEntryAccount">
            <img
              v-if="run.account_domain"
              :src="googleFaviconUrl(run.account_domain, { size: 16 })"
              class="runEntryFavicon"
              alt=""
              loading="lazy"
            />
            <span class="runEntryAccountName">{{ run.account_name || run.account_domain }}</span>
            <span v-if="run.account_name && run.account_domain" class="runEntryDomain">{{ run.account_domain }}</span>
          </div>
          <AgentRunStatusPill :status="run.status" />
        </div>
        <div v-if="run.scoped_contact" class="runEntryContact">
          <AppIcon name="contact" :size="12" />
          <span class="runEntryContactName">{{ run.scoped_contact.name || run.scoped_contact.email || 'Contact' }}</span>
        </div>
        <div class="runEntryMeta">
          <span class="runEntryDate">{{ formatDate(run.created_at) }}</span>
        </div>
        <div v-if="(run.reachouts && run.reachouts.length > 0) || (run.staged_candidates && run.staged_candidates.length > 0)" class="runEntryReachouts">
          <ReachoutPill v-for="rch in run.reachouts" :key="rch.id" :reachout="rch" />
          <router-link
            v-for="cand in run.staged_candidates"
            :key="cand.membership_id"
            :to="cand.kind === 'account' ? `/accounts/${cand.account_id}` : `/contacts/${cand.contact_id}`"
            class="n-pill stagedPill"
            @click.stop
          >
            <AppIcon :name="cand.kind === 'account' ? 'account' : 'contact'" :size="12" />
            {{ cand.name || cand.domain || 'Candidate' }}
          </router-link>
        </div>
        <div v-if="run.error" class="runEntryError">{{ run.error }}</div>
      </router-link>
    </div>

    <template #footer>
      <button class="btn secondary" @click="$emit('close')">Close</button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import Modal from './Modal.vue'
import AgentRunStatusPill from '../../components/AgentRunStatusPill.vue'
import AppIcon from '../../components/AppIcon.vue'
import ReachoutPill from './ReachoutPill.vue'
import { apiFetch } from '../../services/api'
import { googleFaviconUrl } from '../../services/favicons'

const props = defineProps<{
  playId: string
  accountId: string
}>()

defineEmits<{ close: [] }>()

const loading = ref(true)
const runs = ref<any[]>([])

onMounted(async () => {
  try {
    const params = new URLSearchParams({ execution_mode: 'prospect', account_id: props.accountId })
    const resp: any = await apiFetch(`/sales-plays/${props.playId}/runs?${params.toString()}`)
    runs.value = resp?.runs || []
  } catch {
    runs.value = []
  } finally {
    loading.value = false
  }
})

function formatDate(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    + ' · '
    + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}
</script>

<style scoped>
.emptyState {
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  color: var(--color-text-tertiary);
  line-height: var(--lh-loose);
  padding: var(--space-3) 0;
}

.runList {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.runEntry {
  display: block;
  padding: var(--space-3);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-md);
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  transition: background var(--duration-fast) var(--ease-out),
              border-color var(--duration-fast) var(--ease-out);
}
.runEntry:hover {
  background: var(--color-bg-subtle);
  border-color: var(--color-border-strong);
}

.runEntryHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
}

.runEntryAccount {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  overflow: hidden;
}

.runEntryFavicon {
  width: 16px;
  height: 16px;
  border-radius: 2px;
  flex-shrink: 0;
}

.runEntryAccountName {
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-text-primary);
  white-space: nowrap;
}

.runEntryDomain {
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-normal);
  color: var(--color-text-tertiary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.runEntryContact {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 4px;
  color: var(--color-text-secondary);
}

.runEntryContactName {
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  color: var(--color-text-secondary);
}

.runEntryMeta {
  margin-top: 2px;
}

.runEntryDate {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  color: var(--color-text-tertiary);
}

.runEntryReachouts {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1);
  margin-top: var(--space-2);
}

.stagedPill { color: var(--color-text-secondary); }

.runEntryError {
  margin-top: var(--space-2);
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  color: var(--color-danger, #dc2626);
  line-height: var(--lh-normal);
}
</style>
